import * as d3 from 'd3-selection'
import intersection from 'path-intersection'
import * as colors from 'colors'

const labelPadding = 10 // on each side
const minFontSize = 8
const maxFontSize = 32

const stage = (selection, config, maxSize) => {
  selection.each(function (d) {
    let radius = maxSize * .20 // 20% of the available space
      , group = d3.select(this)
                  .selectAll('g.stage-number')
                  .data(d => config.stage ? [d] : [])
      , enter = group.enter()
                     .append('g')
                     .attr('class', 'stage-number')

    group.exit().remove()

    enter.append('circle')
         .attr('r', radius)

    enter.append('text')

    group = group.merge(enter)

    group.selectAll('circle')
         .attr('r', radius)

    group.selectAll('text')
         .text(d => d.data.idx + 1)
         .attr('font-size', radius * 1.5)
  })
}

const setTextVariation = selection => {
  selection.each(function (d) {
    let variation = d.data.textVariation
    d3.select(this).classed(variation, true)
  })
}

const title = selection => {
  selection.each(function (d) {
    let text = d3.select(this)
                  .selectAll('g.text-group-title')
                  .data(d => d.data.title ? [d] : [])
                  .join('g')
                  .attr('class', 'text-group-title')
                  .selectAll('text.title')
                  .data(d => [d])
      , enter = text.enter()
                    .append('text')
                    .attr('class', 'title label-on-colored-background')

    text.exit().remove()

    text.merge(enter)
        .classed('light dark', false)
        .call(setTextVariation)
        .text(d => d.data.title)
        .attr('font-size', d.data.fontSize)
        .select(function (d) {
          let ellipsis = '...'
          // Crop it
          while (Math.floor(this.getComputedTextLength()) > d.data.maxLabelWidth && this.textContent !== ellipsis) {
            d3.select(this).text(`${d3.select(this).text().slice(0, -4)}${ellipsis}`)
          }
        })
  })
}

const value = (selection, config) => {
  selection.each(function (d) {
    let text = d3.select(this)
                  .selectAll('g.text-group-value')
                  .data(d => config.value ? [d] : [])
                  .join('g')
                  .attr('class', 'text-group-value')
                  .selectAll('text.value')
                  .data(d => [d])
      , enter = text.enter()
                    .append('text')
                    .attr('class', 'value label-on-colored-background')

    text.exit().remove()

    text.merge(enter)
        .classed('light dark', false)
        .call(setTextVariation)
        .text(d => d.data.formatter(d.data.raw))
        .attr('font-size', d.data.fontSize)
  })
}

export default (selection, arrows, config) => {
  selection.each(function (d) {
    let container = d3.select(this)
                      .selectAll('g.labels')
                      .data(d => [d])
                      .enter()
                      .append('g')
                        .attr('class', 'labels')
                        .merge(d3.select(this).selectAll('g.labels'))
      , dimensions = d.dimensions
      , labels = container.selectAll('g.slice-group')
                          .data(d => d.slices)
      , width = arrows.outerRadius()() - arrows.innerRadius()() // Width of the arc
      , slices = d.slices
      , arrowDistance = arrows.distance(d.slices[0]) // Distance between tips. Use the first stage, they are all the same size

    if (slices.length === 1) {
      // One continuous stage. The arrowDistance value is wrong since the head
      // and tail are touching
      arrowDistance = width// 2
    }

    // Determine the font size we'll use for the labels
    let fontSizes = []
      , maxSize = Math.min(width, arrowDistance) * .9 // 90% of the available space

    labels.exit().remove()

    labels = labels.enter()
                   .append('g')
                     .attr('class', 'slice-group')
                     .merge(labels)

    // Determine the ideal font size for each stage
    labels.each(function (d) {
      let stub = d3.select(this)
                   .append('text')
                   .attr('class', 'title')
      // Determine the max width for this label. We do this by determining
      // where a horizontal line in the center of the stage intersects
      // its outer arc
      let centroid = arrows.centroid(d)
        , baseline = `M${centroid[0] - dimensions.width},${centroid[1]}L${centroid[0] + dimensions.width},${centroid[1]}`
        , intersected = intersection(arrows(d), baseline)
        , x = intersected.map(p => p.x).sort((a, b) => a - b)
        , width = (x[1] - x[0]) - labelPadding * 2

      stub.text(d.data.title)
          .attr('font-size', '1px') // Initialize

      // Determine the font size that fits in the ideal width
      fontSizes.push(Math.min(width, width / stub.node().getComputedTextLength()))
      d.data.maxLabelWidth = width
      d.data.textVariation = colors.getTextVariation(colors.lookup(d.data.color))

      stub.remove() // This was just a placeholder
    })

    // Now find the min font so we can share it
    let fontSize = Math.min(Math.max(minFontSize, Math.min(...fontSizes)), maxFontSize)

    // Now store the label font size on each stage. This is needed for percentages
    labels.each(stage => {
      stage.data.fontSize = fontSize
    })

    labels.call(stage, config, maxSize)
          .call(title)
          .call(value, config)

    // Now position each label
    labels.each(function (d) {
      let children = this.childNodes
        , yOffset = 0

      children.forEach(child => {
        if (yOffset) {
          d3.select(child).attr('transform', `translate(0, ${yOffset})`)
        }
        yOffset += child.getBBox().height
      })

      let bb = this.getBBox()
        , centroid = arrows.centroid(d)
        , x = centroid[0]
        , y = centroid[1] - (bb.height / 4)

      if (slices.length === 1) {
        x = 0 // Single stage, center it in the bottom
      }

      d3.select(this)
        .attr('transform', `translate(${x}, ${y})`)
    })


  })
}
